import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bot, Plus } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useServiceBotStrategies,
  useServiceBotEnabledSites,
  useDeleteServiceBotStrategy,
  type BotStrategyListItem,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { errMessage } from '@/pages/services/waap/waap-util';
import { useWaapUpgradeGate } from '@/pages/services/waap/use-waap-upgrade-gate';
import { trackFeatureAction } from '@/lib/analytics-events';
import styles from './BotManagementTab.module.css';

const muted = (v: React.ReactNode) => <span className={styles.muted}>{v}</span>;

/**
 * Bot Management — account/product-level list of bot strategies. Each strategy is
 * created once (rich form) and applied to one or more sites ("Apply to"). Backed
 * by the private portal `/bot/bot_strategy` (see api/waap/bot-strategy.ts), not a
 * per-website config — so there is no WebsitePicker here.
 */
export function BotManagementTab() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { show } = useToast();
  const { gate, upgradeDialog } = useWaapUpgradeGate();
  const base = `/services/waap/${serviceId}`;
  const [pendingDelete, setPendingDelete] = useState<BotStrategyListItem | null>(null);

  const { data: strategies = [], isLoading } = useServiceBotStrategies(serviceId);
  const { data: sites = [] } = useServiceBotEnabledSites(serviceId);

  // Resolve site IDs → domain names for the Websites column.
  const siteName = useMemo(() => {
    const m = new Map<number, string>();
    for (const s of sites) m.set(s.id, s.domain);
    return (id: number) => m.get(id) ?? `#${id}`;
  }, [sites]);

  const del = useDeleteServiceBotStrategy(serviceId ?? '', {
    onSuccess: () => {
      // track feature engagement
      trackFeatureAction('waap', 'bot_strategy', 'delete');
      show({ tone: 'success', title: 'Bot management rule deleted' });
      setPendingDelete(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Delete failed', description: errMessage(e) }),
  });

  const columns = useMemo<ColumnDef<BotStrategyListItem>[]>(
    () => [
      { accessorKey: 'id', header: 'ID', cell: ({ row }) => muted(row.original.id) },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => <span className={styles.name}>{row.original.name}</span>,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => row.original.description || muted('—'),
      },
      {
        id: 'websites',
        header: 'Websites',
        cell: ({ row }) => {
          const ids = row.original.sites ?? [];
          return ids.length ? ids.map(siteName).join(', ') : muted('—');
        },
      },
      {
        id: 'actions',
        header: 'Operation',
        cell: ({ row }) => (
          <div className={styles.actions}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`${base}/bot-management/${row.original.id}`)}
            >
              Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setPendingDelete(row.original)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, siteName],
  );

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'Bot Management' }]} />}
        title="Bot Management"
        description="Bot management dynamically protects websites against automated threats from malicious bots."
        actions={
          <Button
            startIcon={<Plus size={16} />}
            onClick={gate('botManagement', 'Bot Management', () =>
              navigate(`${base}/bot-management/new`),
            )}
          >
            Add Bot Management Rule
          </Button>
        }
      />

      <Section title="Bot Management Rules">
        {isLoading ? (
          <div className={styles.skeletonList}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className={styles.skeletonRow} />
            ))}
          </div>
        ) : (
          <div className={styles.tableScroll}>
            <DataTable
              columns={columns}
              data={strategies}
              emptyState={
                <EmptyState
                  size="sm"
                  icon={<Bot size={20} />}
                  title="No bot management rules"
                  description="Add a rule to start protecting your websites from automated bot threats."
                />
              }
            />
          </div>
        )}
      </Section>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete bot management rule?"
        description={`Delete "${pendingDelete?.name}"? This removes bot protection from the websites it applies to. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => pendingDelete && del.mutate(pendingDelete.id)}
      />
      {upgradeDialog}
    </Container>
  );
}
